import { useMutation } from "convex/react";
import { X } from "lucide-react";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import {
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAllDecks } from "@/hooks/useAllDecks";
import { api } from "@convex/_generated/api";
import type { Id } from "@convex/_generated/dataModel";

interface MoveCardDialogContentProps {
  cardId: Id<"cards">;
  currentDeckId: Id<"decks">;
}

export const MoveCardDialogContent = (props: MoveCardDialogContentProps) => (
  <DialogContent className="sm:max-w-md">
    <DialogHeader>
      <DialogTitle className="text-xl">Move Card to Deck</DialogTitle>
      <DialogClose className="absolute right-4 top-4 opacity-70 hover:opacity-100">
        <X className="h-4 w-4" />
      </DialogClose>
    </DialogHeader>
    <DialogDescription hidden={true}>Move this card to a different deck.</DialogDescription>
    <MoveCardDialogContentInternal {...props} />
  </DialogContent>
);

// We store the id of the last selected deck in local storage so it is sticky.
const LAST_SELECTED_DECK_ID_KEY = "moveCardDialogLastSelectedDeckId";

// By nesting MoveCardDialogContentInternal inside MoveCardDialogContent, we can
// ensure that it doesn't render until the dialog is open. This is important because
// We want to get fresh initial state for the selected deck each time it opens.
const MoveCardDialogContentInternal = ({ currentDeckId, cardId }: MoveCardDialogContentProps) => {
  const decks = useAllDecks();
  const [selectedDeck, setSelectedDeck] = useState<string | undefined>(() => {
    const lastSelected = localStorage.getItem(LAST_SELECTED_DECK_ID_KEY) ?? undefined;
    return lastSelected === currentDeckId ? undefined : lastSelected; // If the last selected deck is the current deck, we want to reset it to undefined.
  });

  const moveCardMutation = useMutation(api.cards.update);
  const moveCard = () => {
    void moveCardMutation({ id: cardId, deckId: selectedDeck as Id<"decks"> });
  };
  const handleDeckSelectChange = (value: string) => {
    setSelectedDeck(value);
    localStorage.setItem(LAST_SELECTED_DECK_ID_KEY, value);
  };

  return (
    <div className="space-y-4">
      {decks && (
        <Select value={selectedDeck} onValueChange={handleDeckSelectChange}>
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Select a deck" />
          </SelectTrigger>
          <SelectContent>
            {decks.map((deck) => (
              <SelectItem key={deck._id} value={deck._id} disabled={deck._id === currentDeckId}>
                {deck.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}

      <div className="flex justify-end">
        <DialogClose asChild disabled={!selectedDeck || selectedDeck === currentDeckId}>
          <Button onClick={moveCard}>MOVE</Button>
        </DialogClose>
      </div>
    </div>
  );
};
